import { useState } from 'react';
import { ArrowRight, Plus } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { toast } from '@/hooks/useToast';
import { money, shortDateTime } from '../outpatient/ui';
import { usePharmacyStores } from '../usePharmacyStores';
import IndentsView from './IndentsView';
import NewTransferDialog from './NewTransferDialog';
import ReorderLevelsView from './ReorderLevelsView';
import TransferDetailDialog from './TransferDetailDialog';
import { TransferStatusBadge } from './TransferStatusBadge';
import { useTransferStores, useTransfers } from './useTransfers';
import type { TransferFilter, TransferStore } from './types';

type View = 'indents' | 'transfers' | 'levels';
const VIEWS: Array<{ value: View; label: string }> = [
  { value: 'indents', label: 'Indents' },
  { value: 'transfers', label: 'Transfers' },
  { value: 'levels', label: 'Reorder levels' },
];

const FILTERS: Array<{ value: TransferFilter; label: string }> = [
  { value: 'in_transit', label: 'In transit' },
  { value: 'received', label: 'Received' },
  { value: 'partially_received', label: 'Short' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

/**
 * Pharmacy → 🔄 Store Transfers (pharmacy:transfer): moving stock between this
 * branch's stores — Main Store to the OP/IP pharmacies, and back. Three views:
 * Indents (a store asks for stock), Transfers (stock on the move) and Reorder
 * levels (when a store should ask). docs/HMS_V4_PHARMACY_MULTI_STORE_DESIGN.md §8.
 */
export default function TransfersTab() {
  const [view, setView] = useState<View>('indents');
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const [openTransferId, setOpenTransferId] = useState<number | null>(null);
  const storesQuery = useTransferStores();
  const channelMap = usePharmacyStores().data?.channels;
  const stores = storesQuery.data ?? [];
  const activeStores = stores.filter((s) => s.active);
  const receivingId = channelMap?.receiving;
  // Reorder levels are per store, so that view always has one picked.
  const levelsStoreId = storeId ?? activeStores.find((s) => s.id !== receivingId)?.id ?? activeStores[0]?.id ?? null;

  return (
    <div className="space-y-4">
      {activeStores.length < 2 && storesQuery.isSuccess && (
        <div className="rounded-[var(--radius)] bg-info-soft px-3 py-2 text-[12.5px] text-info-ink">
          This branch has one pharmacy store, so there is nothing to transfer between. Add OP and IP stores in Admin → Pharmacy & Lab → Pharmacy Stores.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl options={VIEWS} value={view} onChange={setView} className="w-full max-w-[380px]" />
        {view !== 'levels' && stores.length > 1 && (
          <select
            aria-label="Store"
            className="h-9 w-auto rounded-[var(--radius)] border border-input bg-card px-2 text-[13px]"
            value={storeId ?? ''}
            onChange={(e) => setStoreId(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">All stores</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>
      {view === 'indents' && (
        <IndentsView
          stores={stores} storeId={storeId} receivingStoreId={receivingId}
          onOpenTransfer={(id) => { setView('transfers'); setOpenTransferId(id); }}
        />
      )}
      {view === 'transfers' && (
        <TransfersListView
          stores={stores} storeId={storeId} defaultFromId={receivingId}
          openId={openTransferId} setOpenId={setOpenTransferId}
        />
      )}
      {view === 'levels' && (
        <ReorderLevelsView stores={stores} storeId={levelsStoreId} receivingStoreId={receivingId} onStoreChange={setStoreId} />
      )}
    </div>
  );
}

function TransfersListView({ stores, storeId, defaultFromId, openId, setOpenId }: {
  stores: TransferStore[];
  storeId?: number;
  defaultFromId?: number;
  openId: number | null;
  setOpenId: (id: number | null) => void;
}) {
  const [filter, setFilter] = useState<TransferFilter>('in_transit');
  const [creating, setCreating] = useState(false);
  const list = useTransfers(filter, storeId);
  const activeStores = stores.filter((s) => s.active);
  const transit = list.data?.in_transit;

  return (
    <>
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SegmentedControl options={FILTERS} value={filter} onChange={setFilter} size="sm" className="w-full max-w-[460px]" />
          {transit && transit.transfers > 0 && (
            <span className="text-[12.5px] text-muted-foreground">
              {transit.transfers} in transit, {transit.units.toLocaleString('en-IN')} units ({money(transit.value_mrp)} at MRP)
            </span>
          )}
          <span className="flex-1" />
          <Button onClick={() => setCreating(true)} disabled={activeStores.length < 2}>
            <Plus className="h-4 w-4" /> New transfer
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow>{['Transfer', 'Route', 'Lines', 'Units', 'Status', 'Issued'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {list.isLoading ? (
                <TableRow><TableCell colSpan={6} className="p-6 text-center">Loading…</TableCell></TableRow>
              ) : list.isError ? (
                <TableRow><TableCell colSpan={6} className="p-6 text-center text-danger">
                  Could not load transfers. <Button variant="ghost" size="sm" onClick={() => list.refetch()}>Try again</Button>
                </TableCell></TableRow>
              ) : !list.data?.transfers.length ? (
                <TableRow><TableCell colSpan={6} className="p-6 text-center text-muted-foreground">
                  {filter === 'in_transit' ? 'Nothing in transit.' : 'No transfers.'}
                </TableCell></TableRow>
              ) : list.data.transfers.map((t) => (
                <TableRow key={t.id} className="cursor-pointer hover:bg-muted" onClick={() => setOpenId(t.id)}>
                  <TableCell className="font-mono text-xs font-semibold">
                    {t.transfer_number}
                    {t.indent_number && <span className="block font-sans text-[11px] font-normal text-muted-foreground">for {t.indent_number}</span>}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">{t.from_store_name} <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /> {t.to_store_name}</span>
                  </TableCell>
                  <TableCell className="tabular-nums">{t.line_count}</TableCell>
                  <TableCell className="tabular-nums">
                    {t.status === 'issued' || t.status === 'cancelled' ? t.units_issued : `${t.units_received} / ${t.units_issued}`}
                  </TableCell>
                  <TableCell><TransferStatusBadge status={t.status} /></TableCell>
                  <TableCell className="text-[12.5px] text-muted-foreground">{shortDateTime(t.issued_at)} · {t.issued_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {creating && (
        <NewTransferDialog
          stores={stores}
          defaultFromId={defaultFromId}
          onClose={() => setCreating(false)}
          onIssued={(t) => {
            setCreating(false);
            toast({
              title: t.status === 'issued' ? `${t.transfer_number} issued — in transit to ${t.to_store_name}` : `${t.transfer_number} moved into ${t.to_store_name}`,
              variant: 'success',
            });
            setOpenId(t.id);
          }}
        />
      )}
      {openId != null && <TransferDetailDialog transferId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
